import { Picker, type PickerOption } from '@clockwise/ui';
import { useQueries } from '@tanstack/react-query';
import * as React from 'react';
import type { EntityPickerConfig } from '@/components/custom/entity-pickers';
import { PickerTruncationNotice } from '@/components/custom/picker-truncation-notice';
import {
  type LoadStrategy,
  useEntityAutocomplete,
} from '@/components/custom/use-entity-autocomplete';

// The entity-bound picker: the shared `Picker` from `@clockwise/ui` plus the
// data layer that turns a `PickerConfig` into its option list. ONE component
// covers both single- and multi-select (mode chosen by the `multiple` prop) for
// any entity — customers, employees (users), departments, expense catalogs —
// so they share this widget instead of forking a near-identical autocomplete
// each.
//
// The SPLIT is the point. Everything visual — the trigger, the portaled
// popover, the search box, the checkmark gutter, the selection chips, the
// keyboard handling — lives in `@clockwise/ui` and knows nothing about
// fetching, which is what lets `@clockwise/report-kit` drive the identical
// control from a static, in-memory vocabulary. What stays here is only the
// async part:
//
//   - 'lazy' (default): debounced server `q=` search, one page at a time —
//     scales to catalogs with thousands of rows (customers, employees).
//   - 'preload': fetch one page on first open, filter in memory — for small
//     catalogs (departments) where client-side filtering is snappier.
//
// Selected ids are resolved to labels via a `useQueries` fan-out over the
// config's `detailKey`/`fetchOne`, cache-shared with every other consumer of
// the same config, so a selection that isn't on the current search page (or
// whose entity was since deleted) still renders and stays removable.
//
// This is the filter/picker control. The type-into-field typeahead
// (`Combobox` / the single-select autocompletes) is a separate, deliberately
// faster control for inline data entry — not replaced by this.

interface EntityPickerBaseProps<T> {
  config: EntityPickerConfig<T>;
  /** See `useEntityAutocomplete`. Defaults to 'lazy' (server search). */
  loadStrategy?: LoadStrategy;
  /** Page size for the one-shot 'preload' fetch. Ignored when lazy. */
  preloadLimit?: number;
  /** Per-page size for 'lazy'/'always' fetches. Defaults to the entity's own
   *  `config.lazyLimit` (see `entity-pickers`) — set this only to override a
   *  particular picker. */
  lazyLimit?: number;
  /** Row body. Defaults to the entity's `getLabel`. The checkmark gutter is
   *  rendered by the shared `Picker`; `renderItem` fills the label area only. */
  renderItem?: (item: T, isSelected: boolean) => React.ReactNode;
  /** Placeholder for the in-popover search input. */
  searchPlaceholder?: string;
  /** Trigger text when nothing is selected. */
  placeholder?: string;
  /**
   * What this picker CHOOSES — "Customer", "Employee". Names the control for a
   * screen reader, which then hears the field and its current value rather than
   * the placeholder. Defaults to the config's `entityLabel`; set this only to
   * override one picker (e.g. "Bill to" for a customer chosen as the payer).
   */
  label?: string;
  /** Popover sizing.
   *  - `'anchor'` (default): fixed width, matching the original picker.
   *  - `'auto'`: use the trigger width as a min but let the popover grow to
   *    fit the widest row (up to a cap). Pick this when rows carry detail the
   *    trigger is too narrow for — e.g. an employee row's email line. */
  popoverWidth?: 'anchor' | 'auto';
  /** Open the popover on mount, once. See `Picker.defaultOpen` — for a picker
   *  that IS the answer to a click that just happened, such as a filter chip
   *  the user has only this moment added to a report's filter bar. */
  defaultOpen?: boolean;
  /**
   * Told when the panel opens and closes.
   *
   * The picker still owns the state — this only reports it. For a caller whose
   * control means something once it has been visited and left: a report filter
   * chip dismissed with nothing chosen is a filter that was never filled in,
   * and the bar takes it back off. */
  onOpenChange?: (open: boolean) => void;
  /** Above the search box. See `Picker.header`. */
  header?: React.ReactNode;
  /** Anchor the panel to an ancestor instead of the trigger. See
   *  `Picker.anchorRef` — for a trigger that is one half of a wider control. */
  anchorRef?: React.RefObject<HTMLElement | null>;
  disabled?: boolean;
  className?: string;
  id?: string;
}

interface SingleProps<T> extends EntityPickerBaseProps<T> {
  multiple?: false;
  value: string | null;
  onChange: (id: string | null, item: T | null) => void;
  /** Show the inline clear (×). Defaults true. */
  clearable?: boolean;
}

interface MultiProps<T> extends EntityPickerBaseProps<T> {
  multiple: true;
  value: readonly string[];
  // ids only: the selected entities aren't always resolved client-side (a
  // chosen id may be off the current search page until its detail query
  // settles), so the component can't reliably hand back parallel item
  // objects. Callers that need the entities resolve them from the shared
  // cache via the same config. Single mode does return the item because its
  // selection is always the just-clicked row.
  onChange: (ids: string[]) => void;
  /** How the trigger handles many chips:
   *  - 'wrap' (default): chips wrap onto as many rows as needed.
   *  - 'scroll': chip area is capped and scrolls — keeps a filter bar's
   *    height stable when sat next to other controls. */
  badgeOverflow?: 'wrap' | 'scroll';
}

export type EntityPickerProps<T> = SingleProps<T> | MultiProps<T>;

export function EntityPicker<T>(props: EntityPickerProps<T>) {
  const {
    config,
    loadStrategy = 'lazy',
    preloadLimit,
    lazyLimit,
    renderItem,
    searchPlaceholder = 'Search…',
    placeholder = 'All',
    label,
    popoverWidth = 'anchor',
    defaultOpen,
    onOpenChange,
    header,
    anchorRef,
    disabled,
    className,
    id,
  } = props;
  const { getId, getLabel, fetchList, fetchOne, listKey, detailKey } = config;
  const searchText = config.searchText ?? getLabel;

  const multiple = props.multiple === true;
  const selectedIds = React.useMemo(
    () => (multiple ? [...props.value] : props.value ? [props.value] : []),
    [multiple, props.value],
  );

  const [query, setQuery] = React.useState('');

  // Single mode drives the hook's single-detail fetch with the current value;
  // multi resolves its set via the `useQueries` fan-out below, so it passes
  // `value: null` to keep the hook's single-detail query idle.
  const auto = useEntityAutocomplete<T>({
    value: multiple ? null : (props.value ?? null),
    strategy: loadStrategy,
    fetchList,
    fetchOne,
    listKey,
    detailKey,
    preloadLimit,
    lazyLimit: lazyLimit ?? config.lazyLimit,
    maxLimit: config.maxLimit,
    excludeCount: multiple ? selectedIds.length : 0,
  });

  // Resolve selected ids → items for the trigger chips and the pinned rows.
  // The chips are visible whenever ids are selected — even with the popover
  // closed (e.g. a fresh page load hydrating from URL params) — so these
  // queries must always run; the cache dedupes repeats.
  const details = useQueries({
    queries: selectedIds.map((sid) => ({
      queryKey: detailKey(sid),
      queryFn: () => fetchOne(sid),
      staleTime: 60_000,
    })),
  });

  const byId = React.useMemo(() => {
    const map = new Map<string, T>();
    for (const it of auto.items) map.set(getId(it), it);
    for (const d of details) {
      const item = d.data?.data;
      if (item) map.set(getId(item), item);
    }
    if (!multiple && auto.selectedItem) map.set(getId(auto.selectedItem), auto.selectedItem);
    return map;
    // `details`/`auto.items` identity changes each query settle — intended.
  }, [auto.items, auto.selectedItem, details, getId, multiple]);

  const selectedSet = React.useMemo(() => new Set(selectedIds), [selectedIds]);

  // Pinned selected rows first (always toggleable), then the search results.
  // Built here rather than left to `Picker`'s own `pinSelected` because a
  // selected id may not be in the current page at all — it exists only in the
  // detail cache — so the list has to be assembled from both sources.
  //
  // Memoized: a fresh array every render would reconcile the whole listbox on
  // each keystroke / detail-query settle. Preload filters its full page in
  // memory (by `searchText`, so non-label fields like qbId/email match); lazy
  // hands back server results as-is. Either way drop already-selected ids —
  // they're pinned separately.
  const options: PickerOption[] = React.useMemo(() => {
    // Mirror the server q semantics (lowercase + whitespace runs collapsed
    // on both sides) so a row is equally findable in preload and lazy
    // modes — e.g. "mary jane" finds a stored "Mary  Jane" either way.
    const norm = (s: string) => s.toLowerCase().replace(/\s+/g, ' ').trim();
    const q = norm(query);
    const results = (
      auto.isClient && q ? auto.items.filter((it) => norm(searchText(it)).includes(q)) : auto.items
    ).filter((it) => !selectedSet.has(getId(it)));
    return [
      ...selectedIds.map((sid, i) => {
        const item = byId.get(sid);
        if (item) return { id: sid, label: getLabel(item) };
        // A selected id whose entity is gone still gets a row, so it stays
        // visible and removable rather than sticking to the value with no way
        // to clear it. But WHICH placeholder depends on whether we have
        // finished looking (G15): '…' while its detail fetch is in flight, and
        // an explicit "Unknown" only once that has settled and the row really
        // is missing — otherwise every filter hydrated from URL params flashes
        // "Unknown" at the user before resolving, which reads as a data gap
        // rather than a slow round trip.
        const settled = details[i] ? !details[i].isPending : false;
        return {
          id: sid,
          label: settled ? `Unknown (${sid.slice(0, 8)}…)` : '…',
        };
      }),
      ...results.map((it) => ({ id: getId(it), label: getLabel(it) })),
    ];
  }, [
    auto.isClient,
    auto.items,
    query,
    selectedIds,
    selectedSet,
    byId,
    details,
    getId,
    getLabel,
    searchText,
  ]);

  const shared = {
    options,
    // The FIELD name, not the placeholder: `Picker` composes the trigger's
    // accessible name as "<title>: <current value>", so passing the placeholder
    // here made every trigger announce "All: All" — and an opt-in prop no call
    // site set left it at "All: Acme Robotics", which names the value but not
    // the field. The entity's own name comes off the config, so customer and
    // employee filters sitting side by side no longer read identically.
    title: label ?? config.entityLabel ?? placeholder,
    placeholder,
    searchPlaceholder,
    // Controlled query ⇒ the shared Picker stops filtering in memory and
    // renders what it's handed. Both modes filter above: lazy on the server
    // (debounced through the hook), preload in memory by `searchText`.
    query,
    onQueryChange: (next: string) => {
      setQuery(next);
      auto.setQuery(next);
    },
    onOpenChange: (open: boolean) => {
      auto.setOpen(open);
      if (!open) {
        setQuery('');
        auto.setQuery('');
      }
      onOpenChange?.(open);
    },
    // `auto.loading` is `isFetching`, split here into Picker's two states.
    // Picker can't split it itself: `options` is ALREADY filtered (we narrow
    // `auto.items` by `searchText` above in preload mode), so an empty list
    // there is either "nothing fetched yet" or "the search matched nothing".
    // Passing the raw flag as `loading` would relabel a mistyped search as
    // "Loading…" for the length of any background revalidation — exactly the
    // "nothing to choose from" lie Picker's `noMatchText` branch was added to
    // kill. So: `loading` only while nothing has been fetched at all, and
    // `refreshing` once a page is in hand. Either one spins the caret.
    // `hasData`, not `items.length`: a page that arrived EMPTY (a search that
    // matched nothing, kept by `keepPreviousData` while the next keystroke
    // fetches) is "fetched nothing", not "nothing fetched yet".
    loading: auto.loading && !auto.hasData,
    refreshing: auto.loading && auto.hasData,
    popoverWidth,
    // The shared Picker fires `onOpenChange(true)` for this on mount, which is
    // what starts the lazy fetch — a panel that opened itself and never asked
    // for data would sit on "Loading…" until it was closed and reopened.
    defaultOpen,
    header,
    anchorRef,
    disabled,
    className,
    id,
    renderOption: renderItem
      ? (option: PickerOption, ctx: { isSelected: boolean }) => {
          const item = byId.get(option.id);
          return item ? (
            renderItem(item, ctx.isSelected)
          ) : (
            <span className="text-muted-foreground">{option.label}</span>
          );
        }
      : undefined,
    // The returned page doesn't cover every match. Say so: an unannotated
    // capped list reads as the complete catalog, so a row that sorts past the
    // cap looks like it doesn't exist — the "customer missing from the report
    // dropdown" bug.
    footer: auto.truncated ? (
      <PickerTruncationNotice truncated={auto.truncated} clientFiltered={auto.isClient} />
    ) : undefined,
  } as const;

  return multiple ? (
    <Picker
      {...shared}
      multiple
      value={props.value}
      onChange={(ids) => (props as MultiProps<T>).onChange(ids)}
      badgeOverflow={(props as MultiProps<T>).badgeOverflow ?? 'wrap'}
      // Already assembled selected-first above, and `Picker` re-sorting it
      // would fight the "unknown id" rows that exist only in this list.
      pinSelected={false}
      // 'clear' only, never 'select-all': in lazy mode the panel holds ONE
      // PAGE of a catalog, so a Select all there would tick what happened to
      // load and silently ignore the rest.
      bulkActions="clear"
    />
  ) : (
    <Picker
      {...shared}
      value={props.value}
      onChange={(pickedId) =>
        (props as SingleProps<T>).onChange(pickedId, pickedId ? (byId.get(pickedId) ?? null) : null)
      }
      clearable={(props as SingleProps<T>).clearable !== false}
    />
  );
}
